import React from 'react';
import { Flex, Heading, Text } from '@qga/roo-ui/components';

import Skeleton from '@shared/v2/components/Skeleton';
import useSearchQuery from '@shared/v2/hooks/useSearchQuery';

import PackageHeroSummary from '@/components/PackageHeroSummary';
import { PackagePagePriceWithPointsPlusPay } from '@/v2/components/pages/PackageView/components/PackagePagePrice';
import PriceContainer from '@/v2/components/pages/PackageView/components/RoomOptions/components/RoomOption/components/CTA/components/PriceContainer';
import { useRoomOptionsData } from '@/v2/components/pages/PackageView/contexts/RoomOptionsContext';
import { CtaButtonStatus } from '@/v2/components/pages/PackageView/hooks/useCtaButtonStatus';

export type PriceProps = {
  buttonStatus: CtaButtonStatus;
};

const REASON_TEXT = {
  changeDates:
    'This package option is unavailable for your selected travel dates.',
  changeTravellers:
    'This package cannot accommodate the selected number of travellers.',
} as const;

const Price = ({ buttonStatus }: PriceProps) => {
  const { searchQuery } = useSearchQuery();
  const { adults, children, infants } = searchQuery || {};

  const { selectedRoomOption } = useRoomOptionsData() || {};
  const offer = selectedRoomOption?.matchingOffer;

  if (buttonStatus === 'changeDates' || buttonStatus === 'changeTravellers') {
    return (
      <Skeleton height="80px">
        <Flex as="aside" gap="1" flexDirection="column">
          <Heading.h6 marginBottom="0" fontWeight="bold" color="brand.primary">
            Unavailable
          </Heading.h6>
          <Text fontSize="sm" fontWeight="bold">
            {REASON_TEXT[buttonStatus]}
          </Text>
        </Flex>
      </Skeleton>
    );
  }

  if (buttonStatus === 'reserve' && offer) {
    return (
      <Skeleton height="80px">
        <PriceContainer includeDivider={false}>
          <PackageHeroSummary searchQuery={searchQuery} showNights exact />
          <PackagePagePriceWithPointsPlusPay
            charges={offer.charges}
            travellers={{ adults, children, infants }}
            offerId={offer.hotelOffer.id}
          />
        </PriceContainer>
      </Skeleton>
    );
  }

  return null;
};

export default Price;
